var __rest = (this && this.__rest) || function (s, e) {
    var t = {};
    for (var p in s) if (Object.prototype.hasOwnProperty.call(s, p) && e.indexOf(p) < 0)
        t[p] = s[p];
    if (s != null && typeof Object.getOwnPropertySymbols === "function")
        for (var i = 0, p = Object.getOwnPropertySymbols(s); i < p.length; i++) {
            if (e.indexOf(p[i]) < 0 && Object.prototype.propertyIsEnumerable.call(s, p[i]))
                t[p[i]] = s[p[i]];
        }
    return t;
};
import { Fragment } from 'react';
import { css } from '@emotion/core';
import { isolateComponent, labelStyles } from '@zapier/style-encapsulation';
import { Heading } from '../../display/Heading';
import { Icon } from '../../display/Icon';
import { Animation, Colors, Shadows, Typography, } from '../../../theme';
const typeConfigs = {
    info: {
        backgroundColor: 'blue',
        color: 'neutral100',
        icon: 'alertInfoFill',
        iconColor: 'blue',
    },
    success: {
        backgroundColor: 'success400',
        color: 'neutral700',
        icon: 'formCheckCircle',
        iconColor: 'success400',
    },
    warning: {
        backgroundColor: 'warning400',
        color: 'neutral700',
        icon: 'alertCircleFill',
        iconColor: 'neutral700',
    },
    error: {
        backgroundColor: 'error400',
        color: 'neutral100',
        icon: 'alertTriangleFill',
        iconColor: 'error400',
    },
};
const Styles = labelStyles('Alert', {
    root: (_a) => {
        var { miniBackgroundColor = 'blue' } = _a, props = __rest(_a, ["miniBackgroundColor"]);
        return [
            props.layout === 'box' && [
                css `
        label: -box;
        position: relative;
        display: grid;
        grid-template-columns: max-content 1fr max-content;
        align-items: start;
        gap: 10px;
        padding: 10px 10px 10px 20px;
        border: 1px solid ${Colors.neutral300};
        border-radius: 5px;
        background-color: ${Colors.neutral100};
        ${Typography.paragraph3};
        color: ${Colors.neutral700};

        // Colored stripe that renders on the left of the box.
        &::before {
          content: '';
          display: block;
          position: absolute;
          top: 0;
          left: 0;
          bottom: 0;
          border-top-left-radius: inherit;
          border-bottom-left-radius: inherit;
          width: 10px;
        }
      `,
                css `
        label: -${props.type};
        &::before {
          background-color: ${Colors[typeConfigs[props.type].backgroundColor]};
        }
      `,
            ],
            props.layout === 'banner' && [
                css `
        label: -banner;
        position: relative;
        display: grid;
        align-items: center;
        ${Typography.paragraph3};
        padding: 10px 44px;
        text-align: center;
      `,
                css `
        label: -${props.type};
        color: ${Colors[typeConfigs[props.type].color]};
        background-color: ${Colors[typeConfigs[props.type].backgroundColor]};

        // Rare tag selection to ensure that links are accessible
        // and consumers don't need to worry about it.
        a[class] {
          color: inherit;
          text-decoration: underline;
          &:hover,
          &:focus {
            color: ${Colors.neutral500};
          }
          &:focus {
            outline: 1px solid ${Colors.neutral500};
          }
        }
      `,
            ],
            props.layout === 'banner-inverse' && [
                css `
        label: -banner;
        position: relative;
        display: grid;
        align-items: center;
        ${Typography.paragraph3};
        padding: 10px 44px;
        text-align: center;
      `,
                css `
        label: -${props.type};
        color: ${Colors.neutral700};
        background-color: ${Colors.neutral100};
      `,
            ],
            props.layout === 'mini' && [
                css `
        label: -mini;
        position: relative;
        display: grid;
        align-items: center;
        ${Typography.paragraph3Medium};
        text-align: center;
        padding: 8px 44px;
      `,
                css `
        label: -${props.type};
        background-color: ${Colors[miniBackgroundColor]};
        color: ${miniBackgroundColor === 'neutral100'
                    ? Colors.neutral800
                    : Colors.neutral100};

        a[class] {
          color: inherit;
          text-decoration: underline;
          &:hover,
          &:focus {
            color: ${Colors.neutral500};
          }
          &:focus {
            outline: 1px solid ${Colors.neutral500};
          }
        }
      `,
            ],
            props.layout === 'modal' && [
                css `
        label: -modal;
        position: relative;
        max-width: 500px;
        display: flex;
        flex-direction: column;
        align-items: center;
        background-color: ${Colors.neutral100};
        padding: 15px;
        border: 1px solid ${Colors.neutral300};
        text-align: center;
        border-radius: 10px;
      `,
            ],
            props.hasShadow &&
                css `
        label: -shadowed;
        box-shadow: ${Shadows.elevation5};
      `,
        ];
    },
    typeIcon: (props) => [
        css `
      color: ${Colors[typeConfigs[props.type].iconColor]};
    `,
        props.layout === 'modal' &&
            css `
        margin-bottom: 8px;
      `,
        (props.layout === 'banner' || props.layout === 'banner-inverse') &&
            css `
        color: ${props.layout === 'banner'
                ? 'currentColor'
                : Colors[typeConfigs[props.type].iconColor]};
        display: inline-block;
        vertical-align: middle;
        margin-right: 5px;
        position: relative;
        top: -2px;
      `,
    ],
    titleInner: (props) => [
        css `
      display: block;
      ${Typography.paragraph3Bold};
      color: ${Colors.neutral800};
    `,
        props.layout === 'banner' &&
            css `
        color: inherit;
      `,
        props.layout === 'mini' &&
            css `
        color: inherit;
      `,
    ],
    closeButton: (props) => [
        props.layout !== 'banner' &&
            props.layout !== 'mini' &&
            css `
        color: ${Colors.neutral600};
        transition: ${Animation.transitionValue};

        &:hover,
        &:focus {
          color: ${Colors.neutral600};
        }
      `,
        props.layout === 'banner' &&
            css `
        color: inherit;
      `,
        props.layout === 'mini' &&
            css `
        color: ${props.miniBackgroundColor === 'neutral100'
                ? Colors.neutral600
                : 'inherit'};
      `,
        (props.layout === 'banner' || props.layout === 'banner-inverse') &&
            css `
        position: absolute;
        right: 15px;
        top: 22px;
        transform: translateY(-50%);
      `,
        props.layout === 'mini' &&
            css `
        position: absolute;
        right: 15px;
        top: 20px;
        transform: translateY(-50%);
      `,
        props.layout === 'modal' &&
            css `
        position: absolute;
        top: 15px;
        right: 15px;
      `,
    ],
});
const defaultProps = {
    hasShadow: true,
    type: 'info',
    layout: 'box',
};
export const Alert = (_props) => {
    const props = Object.assign(Object.assign({}, defaultProps), _props);
    const config = typeConfigs[props.type];
    const isBanner = props.layout === 'banner' || props.layout === 'banner-inverse';
    const isMini = props.layout === 'mini';
    const icon = (isolateComponent("div", { css: Styles.typeIcon(props) },
        isolateComponent(Icon, { ariaLabel: props.type, isBlock: true, name: config.icon, size: 24 })));
    const closeButton = props.onDismiss && (isolateComponent("button", { type: "button", "aria-label": "Dismiss", css: Styles.closeButton(props), onClick: props.onDismiss },
        isolateComponent(Icon, { isBlock: true, name: "formX", size: 24 })));
    const bannerIcon = isBanner && !isMini && icon && (isolateComponent(Fragment, null,
        icon,
        "\u00A0"));
    return (isolateComponent("article", { "aria-live": props.ariaLive, css: Styles.root(props), role: props.role },
        !isBanner && !isMini && icon,
        isolateComponent("div", { css: Styles.content },
            props.title && !isMini && (isolateComponent(Heading, { css: Styles.title },
                isolateComponent("span", { css: Styles.titleInner(props) },
                    bannerIcon,
                    props.title))),
            props.children),
        closeButton));
};
//# sourceMappingURL=Alert.js.map
// @zapier/design-system@5.18.1 (f78072c92)
